import {_, action, computed, computedFn, observable, React, toJS} from '@api'
import {BuilderCardStore, BuilderCardStoreContext, NestedCardItems, SectionalCardFieldRenderer, SectionalCardFieldRendererIndividual} from '@modules/catalog'
import {autorun} from 'mobx'
import * as css from './BuilderCardField.css'
import { CatalogFieldRenderer } from '@modules/CatalogItemDetails'
import {FieldRendererStore} from '@modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/CatalogFieldRendererStore'
import {CardFieldContextMenuItems,  IContextMenuItems} from '@modules/contextMenus'
import {isArrayCatalogField} from '@stores/catalog'
import {DetailsSectionConfig, ICatalogFieldConfig} from '@stores/config'
import {StoresInstance, ui} from '@stores'
import {useContext} from 'react'
import { VipLocalStore } from '@design-board'

export const CardFieldStoreContext = React.createContext<BuilderCardFieldStore>(undefined)
export const useCardFieldStore = () => useContext(CardFieldStoreContext)

export class BuilderCardFieldStore extends VipLocalStore implements IContextMenuItems {
  public constructor(stores: StoresInstance,public field: ICatalogFieldConfig, public cardStore: BuilderCardStore) {
    super(stores)

    this.originalValue = _.clone(this.value) // shallow - we only need a bit for checking dirty
    if (cardStore) {
      cardStore.components.fields[field.field] = this
    }
  }

  @observable.ref originalValue : any

  @computed get isDirty() {
    if (!this.field) return false
    const {field, catalog, value, originalValue} = this

    if (!value?.val && !originalValue?.val) return false
    if (value?.val && !originalValue?.val || !value?.val && originalValue?.val) return true

    if (field.esIndex) {
      return field?.def?.type === 'RefList'
             ? (originalValue.val as Array<any>).every((v, i) => catalog.idFor(v) === catalog.idFor(value.val[i]))
             : catalog.idFor(this.value?.val) != catalog.idFor(this.originalValue?.val)
    }

    return !_.isEqual(this.value?.val, this.originalValue?.val)
  }

  registerEffects = () => {
    const _toDispose = [

    ].filter(_.identity)

    return () => _toDispose.forEach(d => d())
  }

  @computed get associatedWorkspace() {
    return this.catalog.workspaceByIndex[this.field.esIndex]
  }

  @computed get isArray() {
    return _.includes(['RefList', 'TagList', 'EnumList'], this.field?.def?.type)
  }

  @computed get library() {
    if (!this.field) return null
    const {catalog, field: {esIndex}} = this

    if (esIndex) {
      return catalog.libraryByIndex[esIndex]
    }
    return null
  }

  get contextMenuItems() {
    return (
      <BuilderCardStoreContext.Provider value={this.cardStore}>
        <CardFieldStoreContext.Provider value={this}>
          <CardFieldContextMenuItems/>
        </CardFieldStoreContext.Provider>
      </BuilderCardStoreContext.Provider>
    )
  }

  @computed get grouping() { return this.groupBy.groupingForField(this.field)}

  get data() { return this.cardStore.selectedNestedItem ?? this.cardStore.data }

  get originalData() {
    return this.cardStore.detailsStore?.originalData ?? this.cardStore.selectedNestedItem ?? this.cardStore.data
  }

  @action toggleActiveNestedItem = (nestedData: any) => {

  }

  @action promoteNestedItem = (nestedData: any) => {
    this.rendererStore.onChangeSpec(nestedData, (this.rendererStore.field as ICatalogFieldConfig).refField)
  }

  @action removeNestedItem = (nestedData: any) => {
    DEBUG && console.debug(`Remove nested item`, nestedData)

    const {data, field} = this

    const item = _.get(data, field.field)
    if (_.isArray(item)) {
      _.set(data, field.field, item.filter(v => {
        if (!v) return false
        if(typeof v === 'object'){
          return v?.id != nestedData?.id
        }
        return (v != nestedData)
      }))
      console.log(_.get(data, field.field))
    }
  }

  @computed get dataMap() {
    return this.catalog.dataMapForWorkspace(this.associatedWorkspace)
  }
  @observable nestedItemsAsList = false
  @action toggleNestedItemsAsList = (e: React.MouseEvent) => {
    e.stopPropagation()
    this.nestedItemsAsList = !this.nestedItemsAsList
  }

  /**
   * TODO Calculating these twice and diffing them is quite expensive
   */
  @computed get value() {
    if (!this.field) return null

    const {es, field: {field, esField, group, card, def}, cardStore, cardStore: {groupByStore, props: {nested}}} = this,
      data = cardStore.selectedNestedItem ?? cardStore.data,
      {catalog: {perspective, perspectiveSectionConfig, esFieldFor, filter}} = this,
      perspectivePath = perspectiveSectionConfig?.path ?? perspective?.field

    let r = nested?.data && field.startsWith(`${perspectivePath}.`) ? es.readField(nested.data, field.substring(perspectivePath.length + 1), group?.field)
                                                                    : card?.nested && esField === '' ? {val: data, valAsString: ''}
                                                                                                     : es.readField(data, esFieldFor(this.field), group?.field)
    if (_.isArray(r.val)) {
      r = {valAsString: null, val: filter.filterNested(field, r?.val, groupByStore/*, perspective*/)}
    }

    if (def?.type === 'RefList') { // Bad data in gant-dev
      if (r.val && !_.isArray(r.val)) {
        r.val = [r.val]
      }
    }
    return r
  }

  @computed get title() {
    if (!this.field) return null
    const {field: {card, def}, fk, cardStore: {data}, catalog} = this,
      fieldTitle = catalog.labelForField(this.field, '', def?.type === 'TagList' || def?.type === 'EnumList' ? 'plural' : 'singular', fk != null)

    return card?.titleFields?.length
           ? (card.titleFields.map(f => _.get(data, f)).join(', ') || fieldTitle)
           : fieldTitle
  }

  get sectionConfig() {
    if (!this.field) return null
    const {field: {section},catalog} = this

    return section ? catalog.sectionById[section] : null
  }

  @computed get titleText() {
    const {sectionConfig, title, value} = this

    return `${title}${sectionConfig?.viewOptions ? '' : _.isArray(value?.val) ? ` (${value.val.length})` : ''}`
  }

  get isFieldNested() {
    if (!this.field) return false
    const {field: {field, esIndex, card}} = this
    return esIndex != null || card?.nested != null || this.fk != null
  }

  /**
   * Field Foreign Key (might be a parent or grandparent that is the FK and this is some nested field)
   */
  @computed get fk() {
    const {field, catalog: {findFieldInTree}} = this
    return findFieldInTree(field.field, true)
  }

  @computed get textValue() {
    const {isFieldNested, value, field: {def}, fk, catalog} = this,
      {aggregateAttrValueLabelFor, attrValueLabelFor} = catalog

    return _.isString(value.val) ? value.val
                                 : isFieldNested
                                   ? _.isObject(value.val) ? _.get(value.val, fk ? catalog.dataMapForLibrary(catalog.libraryByIndex[fk.esIndex], false).title : 'name') : value.val
                                   : def ? _.isArray(value?.val) ? aggregateAttrValueLabelFor({v: value?.val, def}) : attrValueLabelFor({v: value?.val, def}) : value?.valAsString ?? this.strings.emptyLabel
  }

  @computed get editable() {
    const {field: {field}, cardStore: {detailsStore, props: {nested}}, catalog} = this

    if (!detailsStore || !ui.settings.inlineCardFieldEditing) return false

    return catalog.isFieldEditable(field, () => nested?.data ?? this.data, nested?.field?.esIndex)
  }

  get computedField() {
    return this.field.computed
  }

  get isComputedField() {
    return !!this.computedField
  }

  @computed get secondaries() {
    const {data} = this
    return data?.secondaries ?? []
  }

  get updateOnAssignmentField() {
    return this.field?.updateOnAssignment
  }

  get isUpdateOnAssignment() {
    return !!this.field?.updateOnAssignment
  }

  @computed get isFiltered() {
    const {field, filterValue, filter} = this
    return filter.isFiltered(field, filterValue)
  }

  @computed get isExcluded() {
    const {field, filterValue, filter} = this
    return filter.isExcluded(field, filterValue)
  }

  @computed get filterValue() {
    return _.get(this.data, this.filterField)
  }

  get filter() { return this.catalog.filter }

  @computed get filterField() {
    return this.filter.filterFieldFor(this.field)
  }

  @action onFilterValue = () => {
    const {field, filterValue, filter} = this

    filter.toggleFilterValue({key: field.field, value: filterValue, exclude: false})
  }

  @action filter_excludeValue = () => {
    const {field, filterValue, filter} = this

    //toggleFilterValue {key: "colorway", value: "110F Steel-18-0601 CHARCOAL GRAY", item: Proxy, exclude: true, forceSet: undefined}
    filter.toggleFilterValue({key: field.field, value: filterValue, exclude: true})
  }

  updateOnAssignmentData = computedFn((item, fieldPath, value) => {
    const {data, secondaries, isUpdateOnAssignment, updateOnAssignmentField, field: {section, field}, sectionConfig} = this,
      cloneData = _.cloneDeep( data)

    if (isUpdateOnAssignment && !_.isArray(updateOnAssignmentField)) {

      const {expression: {field: f, assignTo, computeField}} = updateOnAssignmentField,
        d = secondaries?.length ? cloneData?.secondaries : null,
        fPath = computeField ?? field

      cloneData[section]?.some((sec) => {
        if (sectionConfig?.viewOptions && _.get(sec, sectionConfig.viewOptions?.field) === _.get(item, sectionConfig.viewOptions?.field)) {
          _.set(sec, fieldPath, value)
          return true
        }
        return false
      })
      if (assignTo && d) {
        _.set(cloneData, f, assignTo(Array.isArray(d) ? d.length === 0 ? null : d?.map(v => _.isNumber(_.get(v, fPath)) ? _.toNumber(_.get(v, fPath) ?? 0) : _.get(v, fPath)) : 0))
      }
    }
    return cloneData
  })

  @computed get requiredParentField() {
    const {field: {field}, catalog} = this,
      {_fieldByName} = catalog

    let path = field ? field.substring(0, field.lastIndexOf('.')) : null

    while (path) {
      if (_fieldByName[path]?.esIndex) {
        return _fieldByName[path]
      }
      path = path.substring(0, path.lastIndexOf('.'))
    }

    return null
  }

  @computed get parentObject() {
    // Require both source and destination
    const {requiredParentField} = this

    if (requiredParentField) {
      const {cardStore: {data}, originalData} = this
      const originalSpec = _.get(originalData, requiredParentField.field),
        currentSpec = _.get(data, requiredParentField.field)

      const r = !originalSpec || !currentSpec || !_.isEqual(originalSpec?.id, currentSpec?.id) ? null : currentSpec
      //console.log(r, requiredParentField.field, originalSpec, currentSpec, originalSpec != currentSpec, !_.isEqual(originalSpec, currentSpec))
      return r
    }
  }

  @computed get readOnly() {
    const {requiredParentField, parentObject, isComputedField, editable, cardStore, cardStore: {props: {readOnly}}} = this
    if (!cardStore.detailsStore) return true
    return readOnly || isComputedField || editable === false || (requiredParentField && !parentObject)
  }

  @computed get showFieldTitle() {
    const {field: {def}, readOnly, editable, sectionConfig, cardStore: {props: {nested}}} = this
    if (!sectionConfig?.viewOptions) {
      if (def?.type == 'Boolean') {
        return _.isArray(this.value?.val) || (nested?.data && (!editable || readOnly)) // for editable booleans we will show a Switch with an inline label instead
      }
    }
    return true
  }

  @computed get style(): any { return {'--max-lines': this.field.card?.maxLines} }

  @computed get dragHandlers() { return this.cardStore.specItemDragHandlers(this.field) }

  get nestedDragHandlers() { return this.cardStore.specItemDragHandlers }

  rendererStore: FieldRendererStore

  @computed get showNestedCardItems() {
    const {cardStore: {props: {nested}}, field} = this
    return nested && field?.card.nested?.renderer != 'inline'
  }

  @computed get valueComponent() {
    if (!this.field) return null

    const {showNestedCardItems, cardStore: {levelOfDetail, drilldown, workspace, props: {nested, readOnly: cardStoreReadOnly}}, sectionConfig, editable, readOnly, field: f, field: {field, def}, value, catalog} = this

    // console.log({field, nested: toJS(nested), f})
    return levelOfDetail != 1 && !catalog.isDataTableView
           ? null
           : f.esIndex ? f.renderer === 'text' ? <CatalogFieldRenderer className={css.fieldValue} field={{field}} workspace={workspace} nestedData={nested?.data}/> :
                         (_.isArray(value.val) || isArrayCatalogField(f)) ? <NestedCardItems workspace={workspace} className={css.fieldValue} disableSelection field={f} parent={this.data} items={this.value?.val} levelOfDetail={drilldown?.field?.field === field ? 1 : undefined}/>
                                                                          : <CatalogFieldRenderer className={css.fieldValue} field={{field}} workspace={workspace} nestedData={nested?.data}/>
                       : nested?.data ? !editable || readOnly ? this.textValue : <CatalogFieldRenderer className={css.fieldValue} field={{field}} workspace={workspace} nestedData={nested?.data}/>
                          : sectionConfig?.viewOptions
                            ? (<SectionalCardFieldRenderer field={f} sectionConfig={sectionConfig} parent={this.data} readOnly={cardStoreReadOnly}/>)
                            : showNestedCardItems
                              ? <NestedCardItems workspace={workspace} className={css.fieldValue} disableSelection field={f} parent={this.data} items={this.value?.val} levelOfDetail={drilldown?.field?.field === field ? 1 : undefined}/>
                              : (!editable || readOnly) && this.value?.val === null && def?.type != 'Boolean' && !f?.computed?.expression ? this.textValue : <CatalogFieldRenderer field={{field}} workspace={workspace} align="inline" nestedData={nested?.data}/>
  }

  @action removeSpec = () => {
    const {field: {field}, rendererStore} = this
    rendererStore.deleteSpec(field)
  }

  @computed get titleComponent() {
    const {isFieldNested, titleText} = this
    return (
      !isFieldNested
      ? titleText
      : <span className={css.drilldownTitle}>
        <span className={css.title}>
          {titleText}
        </span>
      </span>)
  }

  @observable specPopoverOpen = false

  @computed get sectionNestedOriginalData(): { [id: string]: any } {
    const {sectionConfig, originalData, field, catalog} = this

    if (!sectionConfig?.viewOptions?.field) return originalData

    const nestedData = _.get(originalData, field.section)
    if (nestedData && !_.isArray(nestedData)) {
      console.debug(`${catalog.idFor(originalData)} does not have nested (array) data for supposed nested field ${field.section} - ${field.field}`, {data: toJS(originalData), nestedData: toJS(nestedData), query: `GET ${catalog.index}/_doc/${catalog.idFor(originalData)}`})
      return null
    }
    return _.keyBy(nestedData, d => _.get(d, sectionConfig?.viewOptions?.field))
  }

  @computed get sectionNestedData(): { [id: string]: any } {
    const {sectionConfig, data, field, catalog} = this

    if (!sectionConfig?.viewOptions?.field) return data

    const nestedData = _.get(data, field.section)
    if (nestedData && !_.isArray(nestedData)) {
      console.debug(`${catalog.idFor(data)} does not have nested (array) data for supposed nested field ${field.section} - ${field.field}`, {data: toJS(data), nestedData: toJS(nestedData), query: `GET ${catalog.index}/_doc/${catalog.idFor(data)}`})
      return null
    }
    return _.keyBy(nestedData, d => _.get(d, sectionConfig?.viewOptions?.field))
  }

  originalDataFor = computedFn((section?: DetailsSectionConfig) => {
    const {sectionConfig, sectionNestedOriginalData, originalData} = this
    if (!sectionConfig?.viewOptions?.field || !section) return originalData
    return {[sectionConfig.path]: (sectionNestedOriginalData ?? {})[section.id]}
  })

  dataFor = computedFn((section?: DetailsSectionConfig) => {
    const {sectionConfig, sectionNestedData, data} = this
    if (!sectionConfig?.viewOptions?.field || !section) return data
    return {[sectionConfig.path]: (sectionNestedData ?? {})[section.id]}
  })

  @action onContextMenu = e => {
    this.contextMenu.onComponentContextMenu(e, this)
  }
}



// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCardFieldStore.tsx